import { engine } from "@src/core/engine";
import { createNumberFieldSchema } from "@src/core/engine/helpers/schemaHelpers";
import {
  WaterCharacteristicsDefinitions,
  waterCharacteristicsRepository,
} from "@src/core/engine/technicalParameters/waterCharacteristics/waterCharacteristicsRepository";
import { buildValidationResult } from "@src/core/engine/validationResults/buildValidationResult";
import { buildZodValidationResult } from "@src/core/engine/validationResults/zodValidationResult";
import { toArray } from "@src/core/engine/vector";
import { isNotNil } from "@src/shared/helpers/guards";
import { assert } from "radashi";
import { z } from "zod/v4";
import { Nullish } from "../types";

export type WaterCharacteristicsInfo = {
  mode: "manual";
} & WaterCharacteristicsPreference;

export type WaterCharacteristicsPreference = {
  mode: "manual" | "predefined";
} & Nullish<z.infer<ReturnType<typeof getWaterCharacteristicsSchema>>>;

type WaterCharacteristicsSchemaOptions = {
  definitions?: WaterCharacteristicsDefinitions;
};

export function getWaterCharacteristicsSchema({
  definitions = waterCharacteristicsRepository.getDefinitions(),
}: WaterCharacteristicsSchemaOptions = {}) {
  const schema = z
    .discriminatedUnion("mode", [
      z.object({
        density: createNumberFieldSchema(
          definitions.density,
          getNumberFieldMessages("density"),
        ),
        freezingPoint: createNumberFieldSchema(
          definitions.freezingPoint,
          getNumberFieldMessages("freezingPoint"),
        ),
        kinematicViscosity: createNumberFieldSchema(
          definitions.kinematicViscosity,
          getNumberFieldMessages("kinematicViscosity"),
        ),
        mode: z.literal("manual"),
        specificHeat: createNumberFieldSchema(
          definitions.specificHeat,
          getNumberFieldMessages("specificHeat"),
        ),
      }),
      z.object({
        additives: z
          .string(getMessageKey("additives", "required"))
          .refine(
            (val) =>
              !val ||
              definitions.additives.groups
                .flatMap((group) =>
                  group.options.flatMap((option) => option.id),
                )
                .some((option) => option === val),
            { message: getMessageKey("additives", "invalid") },
          ),
        defaultDifferentialTemperature: createNumberFieldSchema(
          definitions.defaultDifferentialTemperature,
          getNumberFieldMessages("defaultDifferentialTemperature"),
        ),
        mode: z.literal("predefined"),
        supplyTemperature: createNumberFieldSchema(
          definitions.supplyTemperature,
          getNumberFieldMessages("supplyTemperature"),
        ),
        weight: createNumberFieldSchema(
          definitions.weight,
          getNumberFieldMessages("weight"),
        ).nullish(),
      }),
    ])
    .superRefine(
      (data, ctx) => {
        if (
          data.mode === "predefined" &&
          !!data.additives &&
          data.additives !== "WATER" &&
          !data.weight
        ) {
          ctx.addIssue({
            code: "custom",
            message: getMessageKey(
              "weight",
              data.weight === 0 ? "tooLow" : "required",
            ),
            path: ["weight"],
          });
        }
      },
      { when: () => true },
    )
    .superRefine((data, ctx) => {
      const technicalParameters = new engine.TechnicalParameters();
      const waterCharacteristics =
        technicalParameters.getWaterCharacteristicsParams();
      assert(
        waterCharacteristics,
        "waterCharacteristics is null in waterCharacteristicsRepository.validate",
      );
      waterCharacteristicsRepository.init(waterCharacteristics, data);
      const waterCharacteristicsErrors = toArray(
        waterCharacteristics.getErrorFields(),
      )
        .map(buildValidationResult)
        .filter(isNotNil);
      technicalParameters.delete();
      if (waterCharacteristicsErrors.length) {
        for (const error of waterCharacteristicsErrors) {
          ctx.addIssue(
            buildZodValidationResult(error, {
              pathMapping: waterCharacteristicsPathMapping,
            }),
          );
        }
      }
    });

  return schema;
}

const waterCharacteristicsPathMapping: Record<string, string> = {
  density: "density",
  diffTemp: "defaultDifferentialTemperature",
  freezPoint: "freezingPoint",
  kinViscosity: "kinematicViscosity",
  pcWeight: "weight",
  specificHeat: "specificHeat",
  supplyTemp: "supplyTemperature",
};

function getMessageKey(field: string, error: string) {
  return `technicalPreferences.waterCharacteristics.fields.${field}.errors.${error}`;
}

function getNumberFieldMessages(field: string) {
  return {
    maxMessage: getMessageKey(field, "tooHigh"),
    minMessage: getMessageKey(field, "tooLow"),
    numberMessage: getMessageKey(field, "required"),
  };
}
